'use strict';

/**
 * Client-side intra-community VAT number validation.
 *
 * Deliberate mirror of the server helper
 * `app_storefront_hf/cartridge/scripts/helpers/vatNumberHelper.js`: identical
 * logic kept in sync by hand so the storefront gives immediate, network-free
 * feedback (no ajax round-trip) while the server keeps the authoritative check.
 * Both files are pure JS (no `dw/*`), so the two stay byte-for-byte aligned —
 * any change here MUST be applied to the server twin (and its unit test), and
 * vice versa.
 *
 * Local validation only (no VIES call):
 *  - structural format check per EU member state (+ Northern Ireland "XI"),
 *  - reinforced check for France: the VAT control key is recomputed from the
 *    SIREN (DGFiP formula) and cross-checked against the SIRET field.
 */

/**
 * Structural patterns per country prefix, applied to the normalized value
 * (uppercase, separators stripped). Based on the official EU VAT number
 * structures. "EL" is Greece and "XI" is Northern Ireland.
 * @type {Object.<string, RegExp>}
 */
var VAT_PATTERNS = {
    AT: /^ATU[0-9]{8}$/,
    BE: /^BE[0-1][0-9]{9}$/,
    BG: /^BG[0-9]{9,10}$/,
    CY: /^CY[0-9]{8}[A-Z]$/,
    CZ: /^CZ[0-9]{8,10}$/,
    DE: /^DE[0-9]{9}$/,
    DK: /^DK[0-9]{8}$/,
    EE: /^EE[0-9]{9}$/,
    EL: /^EL[0-9]{9}$/,
    ES: /^ES[0-9A-Z][0-9]{7}[0-9A-Z]$/,
    FI: /^FI[0-9]{8}$/,
    FR: /^FR[0-9]{11}$/,
    HR: /^HR[0-9]{11}$/,
    HU: /^HU[0-9]{8}$/,
    IE: /^IE([0-9]{7}[A-Z]{1,2}|[0-9][A-Z0-9+*][0-9]{5}[A-Z])$/,
    IT: /^IT[0-9]{11}$/,
    LT: /^LT([0-9]{9}|[0-9]{12})$/,
    LU: /^LU[0-9]{8}$/,
    LV: /^LV[0-9]{11}$/,
    MT: /^MT[0-9]{8}$/,
    NL: /^NL[A-Z0-9]{12}$/,
    PL: /^PL[0-9]{10}$/,
    PT: /^PT[0-9]{9}$/,
    RO: /^RO[0-9]{2,10}$/,
    SE: /^SE[0-9]{12}$/,
    SI: /^SI[0-9]{8}$/,
    SK: /^SK[0-9]{10}$/,
    XI: /^XI([0-9]{9}|[0-9]{12}|GD[0-9]{3}|HA[0-9]{3})$/
};

var SEPARATORS = /[\s.-]/g;

/**
 * Normalizes a raw VAT number: uppercase and removal of spaces, dots and dashes.
 * @param {string} value - Raw VAT number as entered by the user.
 * @returns {string} Normalized VAT number (may be empty).
 */
function normalize(value) {
    return String(value || '').toUpperCase().replace(SEPARATORS, '');
}

/**
 * Checks the structural format of a normalized VAT number against its country pattern.
 * @param {string} normalized - Normalized VAT number (uppercase, no separators).
 * @returns {boolean} True when the prefix is a known country and the structure matches.
 */
function hasValidFormat(normalized) {
    var pattern = VAT_PATTERNS[normalized.substring(0, 2)];
    return !!pattern && pattern.test(normalized);
}

/**
 * Reinforced validation for French VAT numbers.
 *
 * The French VAT number is "FR" + a 2-digit control key + the 9-digit SIREN.
 * The key is always numeric (`(12 + 3 * (SIREN mod 97)) mod 97`, i.e. 00-96), so
 * it is recomputed from the SIREN and compared directly. When a SIRET is
 * provided, the SIREN embedded in the VAT number must also match the
 * SIRET-derived SIREN.
 * @param {string} normalizedVat - Normalized VAT number (expected "FR" + 11 digits).
 * @param {string} siret - SIRET entered in the companion field (raw or normalized).
 * @returns {boolean} True when the French VAT number is consistent.
 */
function hasValidFrenchKey(normalizedVat, siret) {
    if (!/^FR[0-9]{11}$/.test(normalizedVat)) {
        return false;
    }

    var key = parseInt(normalizedVat.substring(2, 4), 10);
    var vatSiren = normalizedVat.substring(4, 13);
    var expectedSiren = String(siret || '').replace(SEPARATORS, '').substring(0, 9);

    if (/^[0-9]{9}$/.test(expectedSiren) && vatSiren !== expectedSiren) {
        return false;
    }

    var expectedKey = (12 + (3 * (parseInt(vatSiren, 10) % 97))) % 97;
    return key === expectedKey;
}

/**
 * Validates an intra-community VAT number.
 * @param {string} rawVat - Raw VAT number as entered by the user.
 * @param {string} [siret] - SIRET from the companion field, used for the FR cross-check.
 * @returns {{valid: boolean, normalized: string, reason: string}} Validation result.
 *  `reason` is one of: 'empty', 'format', 'fr-key-mismatch', '' (valid).
 */
function validate(rawVat, siret) {
    var normalized = normalize(rawVat);

    if (!normalized) {
        return { valid: false, normalized: '', reason: 'empty' };
    }
    if (!hasValidFormat(normalized)) {
        return { valid: false, normalized: normalized, reason: 'format' };
    }
    if (normalized.indexOf('FR') === 0 && !hasValidFrenchKey(normalized, siret)) {
        return { valid: false, normalized: normalized, reason: 'fr-key-mismatch' };
    }

    return { valid: true, normalized: normalized, reason: '' };
}

module.exports = {
    normalize: normalize,
    hasValidFormat: hasValidFormat,
    hasValidFrenchKey: hasValidFrenchKey,
    validate: validate
};
